import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Sun, Battery, Gauge, Plug, Zap, Power } from 'lucide-react'
import { getMyDeviceMetrics, type MyDevice } from '../api/energy-os'

// Glass card grid replacing the old fixed-slot house illustration (EnergyOSHomeCanvas) — every
// device is added and displayed the same way now (Phase 5d-2), so there's no more "slotted vs
// overflow" split by type.
const TYPE_ICONS: Record<string, typeof Sun> = {
  solar: Sun,
  battery: Battery,
  meter: Gauge,
  appliance: Plug,
}

const METRIC_ICONS: Record<string, typeof Zap> = {
  power: Zap,
  powerKw: Zap,
  soc: Battery,
  voltage: Power,
}

function DeviceCard({ device }: { device: MyDevice }) {
  const { data: readings } = useQuery({
    queryKey: ['my-device-latest', device.deviceCoffeeId],
    queryFn: () => getMyDeviceMetrics(device.deviceCoffeeId).then((r) => r.data),
    refetchInterval: 30_000,
  })

  // Brainer's children listing has no guaranteed order — find the latest reading per metric
  // explicitly rather than assuming array order, same pattern used server-side.
  const latestByMetric = new Map<string, { value: number; unit: string; timestamp: string }>()
  for (const r of readings ?? []) {
    const existing = latestByMetric.get(r.metric)
    if (!existing || r.timestamp > existing.timestamp) latestByMetric.set(r.metric, { value: r.value, unit: r.unit, timestamp: r.timestamp })
  }

  const Icon = TYPE_ICONS[device.deviceType] ?? Zap

  return (
    <Link
      to={`/energy-os/dashboard/devices/${device.deviceCoffeeId}`}
      className="card group flex flex-col gap-4 p-5 hover:shadow-[0_12px_36px_-8px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition-all"
    >
      <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-paper-sunken px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-dim">
        <Icon size={12} />
        {device.deviceType}
      </span>

      <div className="min-w-0">
        <p className="text-base font-semibold text-ink truncate">{device.label}</p>
        {device.subType && <p className="text-xs text-ink-dim truncate">{device.subType}</p>}
      </div>

      <div className="flex flex-wrap gap-2 min-h-[26px]">
        {[...latestByMetric.entries()].map(([metric, r]) => {
          const MetricIcon = METRIC_ICONS[metric] ?? Zap
          return (
            <span key={metric} className="inline-flex items-center gap-1 rounded-full bg-paper-sunken px-2 py-1 text-theme-xs font-medium text-ink-dim">
              <MetricIcon size={12} className="text-ink-dim" />
              {r.value} {r.unit}
            </span>
          )
        })}
        {latestByMetric.size === 0 && <span className="text-theme-xs text-ink-dim">No readings yet</span>}
      </div>
    </Link>
  )
}

export function EnergyOSDeviceGrid({ devices }: { devices: MyDevice[] }) {
  if (devices.length === 0) {
    return <p className="text-xs text-ink-dim text-center py-10">No devices added yet</p>
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {devices.map((d) => <DeviceCard key={d.id} device={d} />)}
    </div>
  )
}
